import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { AthleteProfile } from '../types';
import { Target, Flame, Activity, Crosshair, BarChart2, Zap, Sparkles, Filter, Info } from 'lucide-react';
import { ProRestrictedWrapper } from './common/ProRestrictedWrapper';

interface ShotDataPoint {
  id: string;
  x: number; // 0 to 500 (feet translated to SVG space)
  y: number; // 0 to 470
  made: boolean;
  type: '2PT' | '3PT' | 'FT' | 'Dunk/Layup';
  zoneName: string;
  distanceFt: number;
}

interface ZoneStat {
  id: string;
  name: string;
  attempts: number;
  makes: number;
  fgPct: number;
  freqPct: number;
  rating: 'hot' | 'warm' | 'cool' | 'cold';
}

interface BasketballCourtHeatmapD3Props {
  user: AthleteProfile;
  onOpenProCheckout?: (contextMessage?: string) => void;
}

export const BasketballCourtHeatmapD3: React.FC<BasketballCourtHeatmapD3Props> = ({ user, onOpenProCheckout }) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [viewMode, setViewMode] = useState<'density' | 'efficiency' | 'scatter'>('efficiency');
  const [shotFilter, setShotFilter] = useState<'all' | 'makes' | 'misses'>('all');
  const [hoveredZone, setHoveredZone] = useState<ZoneStat | null>(null);
  const [selectedZone, setSelectedZone] = useState<ZoneStat | null>(null);

  // Derive shot distribution based on user stats
  const totalFg = Math.max(20, user.stats.basketball.fgAttempted || 120);
  const totalMade = Math.max(10, user.stats.basketball.fgMade || 62);
  const threeAtt = Math.max(5, user.stats.basketball.threeAttempted || 45);
  const threeMade = Math.max(2, user.stats.basketball.threeMade || 18);

  // Generate deterministic shot positions based on user profile and basketball stats
  const generateShotData = (): { shots: ShotDataPoint[]; zones: ZoneStat[] } => {
    const shots: ShotDataPoint[] = [];

    // Court dimensions in SVG coordinates: width = 500, height = 470
    // Hoop is at x = 250, y = 52.5 (4.75ft from baseline y=0)
    const hoopX = 250;
    const hoopY = 52.5;

    // Helper to calculate distance in feet (10 SVG units = 1 foot)
    const getDistanceFt = (x: number, y: number) => {
      const dx = (x - hoopX) / 10;
      const dy = (y - hoopY) / 10;
      return Math.round(Math.sqrt(dx * dx + dy * dy) * 10) / 10;
    };

    // Zone shot allocation weights based on total stats
    const zoneDefinitions = [
      { id: 'paint', name: 'Restricted Paint & Rim', xRange: [200, 300], yRange: [20, 140], weight: 0.32, makeRate: 0.64 },
      { id: 'floater', name: 'In The Paint / Floater Zone', xRange: [180, 320], yRange: [140, 210], weight: 0.16, makeRate: 0.48 },
      { id: 'mid_left', name: 'Mid-Range Left Baseline/Elbow', xRange: [70, 180], yRange: [60, 220], weight: 0.12, makeRate: 0.44 },
      { id: 'mid_center', name: 'High Post / Free Throw Line', xRange: [190, 310], yRange: [210, 270], weight: 0.10, makeRate: 0.46 },
      { id: 'mid_right', name: 'Mid-Range Right Baseline/Elbow', xRange: [320, 430], yRange: [60, 220], weight: 0.12, makeRate: 0.42 },
      { id: 'corner_3_left', name: 'Left Corner 3PT', xRange: [20, 60], yRange: [20, 140], weight: 0.05, makeRate: 0.40 },
      { id: 'wing_3_left', name: 'Left Wing 3PT', xRange: [60, 160], yRange: [220, 340], weight: 0.05, makeRate: 0.38 },
      { id: 'top_3', name: 'Top of Key 3PT', xRange: [170, 330], yRange: [290, 390], weight: 0.04, makeRate: 0.36 },
      { id: 'wing_3_right', name: 'Right Wing 3PT', xRange: [340, 440], yRange: [220, 340], weight: 0.03, makeRate: 0.37 },
      { id: 'corner_3_right', name: 'Right Corner 3PT', xRange: [440, 480], yRange: [20, 140], weight: 0.01, makeRate: 0.35 },
    ];

    let currentShotId = 1;
    const zones: ZoneStat[] = [];

    zoneDefinitions.forEach((z) => {
      const zoneAttempts = Math.max(4, Math.round(totalFg * z.weight));
      let zoneMakes = 0;

      for (let i = 0; i < zoneAttempts; i++) {
        // Random point within zone ranges with slight clustering toward basket
        const x = Math.floor(Math.random() * (z.xRange[1] - z.xRange[0])) + z.xRange[0];
        const y = Math.floor(Math.random() * (z.yRange[1] - z.yRange[0])) + z.yRange[0];
        const dist = getDistanceFt(x, y);

        // Determine if shot is made
        const madeSeed = Math.random();
        const is3Pt = z.id.includes('3');
        const effectiveMakeRate = is3Pt ? (threeAtt > 0 ? threeMade / threeAtt : z.makeRate) : z.makeRate;
        const made = madeSeed < effectiveMakeRate;

        if (made) zoneMakes++;

        shots.push({
          id: `shot_${currentShotId++}`,
          x,
          y,
          made,
          type: is3Pt ? '3PT' : z.id === 'paint' ? 'Dunk/Layup' : '2PT',
          zoneName: z.name,
          distanceFt: dist,
        });
      }

      const fgPct = zoneAttempts > 0 ? Math.round((zoneMakes / zoneAttempts) * 100) : 0;
      const freqPct = Math.round((zoneAttempts / totalFg) * 100);

      let rating: 'hot' | 'warm' | 'cool' | 'cold' = 'warm';
      if (fgPct >= 52) rating = 'hot';
      else if (fgPct >= 42) rating = 'warm';
      else if (fgPct >= 35) rating = 'cool';
      else rating = 'cold';

      zones.push({
        id: z.id,
        name: z.name,
        attempts: zoneAttempts,
        makes: zoneMakes,
        fgPct,
        freqPct,
        rating,
      });
    });

    return { shots, zones };
  };

  const { shots, zones } = generateShotData();

  // Find overall highest frequency position / preferred court zone
  const preferredZone = [...zones].sort((a, b) => b.attempts - a.attempts)[0];

  // Draw Interactive Basketball Court with D3
  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;

    const width = 500;
    const height = 470;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    svg
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    // 1. Court Wood Floor Background
    const courtBgGroup = svg.append('g').attr('class', 'court-background');

    courtBgGroup
      .append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('rx', 16)
      .attr('fill', '#0B0F2A')
      .attr('stroke', '#312E81')
      .attr('stroke-width', 2);

    // Court inner hardwood floor shading
    courtBgGroup
      .append('rect')
      .attr('x', 15)
      .attr('y', 15)
      .attr('width', width - 30)
      .attr('height', height - 30)
      .attr('fill', '#11173d')
      .attr('rx', 8);

    // 2. Court Markings & Lines Group
    const linesGroup = svg.append('g').attr('class', 'court-lines').attr('stroke', '#a5b4fc').attr('stroke-width', 2).attr('fill', 'none');

    // Key / Paint Area (160px wide, 190px tall centered at x=250)
    linesGroup
      .append('rect')
      .attr('x', 170)
      .attr('y', 15)
      .attr('width', 160)
      .attr('height', 190)
      .attr('fill', '#1e1b4b')
      .attr('fill-opacity', 0.6);

    // Free Throw Circle (Radius = 60px)
    linesGroup
      .append('path')
      .attr('d', 'M 190,205 A 60,60 0 0,0 310,205')
      .attr('stroke-dasharray', '4,4');

    linesGroup
      .append('path')
      .attr('d', 'M 190,205 A 60,60 0 0,1 310,205');

    // Restricted Area Arc (Radius = 40px under hoop)
    linesGroup
      .append('path')
      .attr('d', 'M 210,52.5 A 40,40 0 0,0 290,52.5');

    // Backboard (60px wide at y=40)
    linesGroup
      .append('line')
      .attr('x1', 220)
      .attr('y1', 40)
      .attr('x2', 280)
      .attr('y2', 40)
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 3);

    // Hoop Rim (18px diameter circle at x=250, y=52.5)
    linesGroup
      .append('circle')
      .attr('cx', 250)
      .attr('cy', 52.5)
      .attr('r', 9)
      .attr('stroke', '#f97316')
      .attr('stroke-width', 3)
      .attr('fill', 'none');

    // Hoop Connector
    linesGroup
      .append('line')
      .attr('x1', 250)
      .attr('y1', 40)
      .attr('x2', 250)
      .attr('y2', 43.5)
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 2);

    // Three-Point Line
    // Straight corner lines (from y=15 down to y=155 at x=50 and x=450)
    linesGroup.append('line').attr('x1', 50).attr('y1', 15).attr('x2', 50).attr('y2', 155);
    linesGroup.append('line').attr('x1', 450).attr('y1', 15).attr('x2', 450).attr('y2', 155);

    // 3PT Arc (Radius = 237.5px centered at hoop)
    linesGroup
      .append('path')
      .attr('d', 'M 50,155 A 237.5,237.5 0 0,0 450,155');

    // Half Court Line & Center Circle Arc
    linesGroup.append('line').attr('x1', 15).attr('y1', 455).attr('x2', 485).attr('y2', 455);
    linesGroup.append('path').attr('d', 'M 190,455 A 60,60 0 0,1 310,455');

    // 3. Render D3 Heatmap Layers
    const heatmapGroup = svg.append('g').attr('class', 'heatmap-layer');

    // Filter shots according to make/miss toggle
    const filteredShots = shots.filter((s) => {
      if (shotFilter === 'makes') return s.made;
      if (shotFilter === 'misses') return !s.made;
      return true;
    });

    if (viewMode === 'density' || viewMode === 'efficiency') {
      // D3 Zone Polygons with Color Scales
      const zoneShapes = [
        { id: 'paint', path: 'M 170,15 L 330,15 L 330,205 L 170,205 Z' },
        { id: 'floater', path: 'M 170,205 L 330,205 L 330,260 L 170,260 Z' },
        { id: 'mid_left', path: 'M 50,15 L 170,15 L 170,260 L 50,155 Z' },
        { id: 'mid_center', path: 'M 170,260 L 330,260 L 330,290 A 237.5,237.5 0 0,1 170,290 Z' },
        { id: 'mid_right', path: 'M 330,15 L 450,15 L 450,155 L 330,260 Z' },
        { id: 'corner_3_left', path: 'M 15,15 L 50,15 L 50,155 L 15,155 Z' },
        { id: 'wing_3_left', path: 'M 15,155 L 50,155 A 237.5,237.5 0 0,0 180,380 L 120,455 L 15,455 Z' },
        { id: 'top_3', path: 'M 180,380 A 237.5,237.5 0 0,0 320,380 L 380,455 L 120,455 Z' },
        { id: 'wing_3_right', path: 'M 320,380 A 237.5,237.5 0 0,0 450,155 L 485,155 L 485,455 L 380,455 Z' },
        { id: 'corner_3_right', path: 'M 450,15 L 485,15 L 485,155 L 450,155 Z' },
      ];

      // D3 Color interpolators
      const efficiencyColorScale = (fgPct: number) => {
        if (fgPct >= 55) return '#a3e635'; // Lime-400
        if (fgPct >= 45) return '#38bdf8'; // Sky-400
        if (fgPct >= 38) return '#facc15'; // Yellow-400
        return '#f43f5e'; // Rose-500
      };

      const densityColorScale = (freqPct: number) => {
        if (freqPct >= 25) return '#f43f5e'; // Heavy red/pink
        if (freqPct >= 15) return '#f97316'; // Orange
        if (freqPct >= 8) return '#eab308'; // Yellow
        return '#38bdf8'; // Sky blue
      };

      zoneShapes.forEach((shape) => {
        const zData = zones.find((z) => z.id === shape.id);
        if (!zData) return;

        const fillColor = viewMode === 'efficiency'
          ? efficiencyColorScale(zData.fgPct)
          : densityColorScale(zData.freqPct);

        const zonePath = heatmapGroup
          .append('path')
          .attr('d', shape.path)
          .attr('fill', fillColor)
          .attr('fill-opacity', hoveredZone?.id === shape.id ? 0.75 : 0.45)
          .attr('stroke', hoveredZone?.id === shape.id ? '#ffffff' : '#312e81')
          .attr('stroke-width', hoveredZone?.id === shape.id ? 3 : 1)
          .style('cursor', 'pointer')
          .style('transition', 'all 0.15s ease-out');

        // Hover events
        zonePath
          .on('mouseenter', () => {
            setHoveredZone(zData);
          })
          .on('mouseleave', () => {
            setHoveredZone(null);
          })
          .on('click', () => {
            setSelectedZone(zData);
          });
      });

      // Overlay Radial Density Glow Points at Zone Centers
      filteredShots.forEach((shot) => {
        heatmapGroup
          .append('circle')
          .attr('cx', shot.x)
          .attr('cy', shot.y)
          .attr('r', 8)
          .attr('fill', shot.made ? '#a3e635' : '#f43f5e')
          .attr('fill-opacity', 0.25)
          .attr('pointer-events', 'none');
      });
    }

    // 4. Scatter Plot Individual Shot Markers Mode
    const scatterGroup = svg.append('g').attr('class', 'scatter-layer');

    filteredShots.forEach((shot) => {
      if (shot.made) {
        // Made shot: Green circle with glow
        scatterGroup
          .append('circle')
          .attr('cx', shot.x)
          .attr('cy', shot.y)
          .attr('r', viewMode === 'scatter' ? 5.5 : 3)
          .attr('fill', '#a3e635')
          .attr('stroke', '#0b0f2a')
          .attr('stroke-width', 1.5)
          .attr('fill-opacity', 0.9);
      } else {
        // Missed shot: Red X cross
        const crossSize = viewMode === 'scatter' ? 4 : 2.5;
        scatterGroup
          .append('line')
          .attr('x1', shot.x - crossSize)
          .attr('y1', shot.y - crossSize)
          .attr('x2', shot.x + crossSize)
          .attr('y2', shot.y + crossSize)
          .attr('stroke', '#f43f5e')
          .attr('stroke-width', 2);

        scatterGroup
          .append('line')
          .attr('x1', shot.x + crossSize)
          .attr('y1', shot.y - crossSize)
          .attr('x2', shot.x - crossSize)
          .attr('y2', shot.y + crossSize)
          .attr('stroke', '#f43f5e')
          .attr('stroke-width', 2);
      }
    });

  }, [viewMode, shotFilter, hoveredZone]);

  return (
    <ProRestrictedWrapper
      user={user}
      onOpenProCheckout={onOpenProCheckout}
      featureTitle="D3 Basketball Court Spatial Heatmap & Shot Hotspots"
      featureDescription="Unlock D3 court spatial mapping and zone efficiency breakdowns against league benchmarks."
      upsellMessage="This historical performance deep-dive requires a PRO membership to unlock."
    >
      <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-6 text-white">
      {/* Section Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-white/10 pb-4">
        <div>
          <div className="flex items-center space-x-2">
            <Crosshair className="w-6 h-6 text-lime-400" />
            <h2 className="text-xl font-black italic uppercase text-white tracking-tight">
              D3 Basketball Court Position Preference & Shot Heatmap
            </h2>
          </div>
          <p className="text-xs text-indigo-200/70 font-semibold mt-0.5">
            Real-time court position distribution, hot zones, and field goal percentage density
          </p>
        </div>

        {/* Heatmap Mode Controls */}
        <div className="flex flex-wrap items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-indigo-950 p-1 rounded-2xl border border-white/10">
            <button
              onClick={() => setViewMode('efficiency')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
                viewMode === 'efficiency'
                  ? 'bg-lime-400 text-black shadow-md'
                  : 'text-indigo-200/60 hover:text-white'
              }`}
            >
              Efficiency (FG %) 🎯
            </button>
            <button
              onClick={() => setViewMode('density')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
                viewMode === 'density'
                  ? 'bg-rose-500 text-white shadow-md'
                  : 'text-indigo-200/60 hover:text-white'
              }`}
            >
              Position Volume 🔥
            </button>
            <button
              onClick={() => setViewMode('scatter')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
                viewMode === 'scatter'
                  ? 'bg-sky-400 text-black shadow-md'
                  : 'text-indigo-200/60 hover:text-white'
              }`}
            >
              Individual Shots 📍
            </button>
          </div>

          {/* Shot Filter Toggle */}
          <div className="flex items-center bg-indigo-950 p-1 rounded-2xl border border-white/10">
            <button
              onClick={() => setShotFilter('all')}
              className={`px-2.5 py-1 rounded-xl text-[11px] font-black uppercase transition ${
                shotFilter === 'all' ? 'bg-indigo-800 text-white' : 'text-indigo-300/60 hover:text-white'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setShotFilter('makes')}
              className={`px-2.5 py-1 rounded-xl text-[11px] font-black uppercase transition ${
                shotFilter === 'makes' ? 'bg-lime-400 text-black' : 'text-indigo-300/60 hover:text-white'
              }`}
            >
              Makes
            </button>
            <button
              onClick={() => setShotFilter('misses')}
              className={`px-2.5 py-1 rounded-xl text-[11px] font-black uppercase transition ${
                shotFilter === 'misses' ? 'bg-rose-500 text-white' : 'text-indigo-300/60 hover:text-white'
              }`}
            >
              Misses
            </button>
          </div>
        </div>
      </div>

      {/* Primary Highlight Badges */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 flex items-center space-x-3">
          <div className="p-3 bg-rose-500/20 text-rose-400 rounded-xl">
            <Flame className="w-6 h-6" />
          </div>
          <div>
            <span className="text-[10px] text-indigo-300/70 uppercase font-black tracking-wider block">
              Primary Court Preference
            </span>
            <span className="text-sm font-black italic text-white uppercase">
              {preferredZone?.name || 'Paint / Rim Slasher'}
            </span>
            <span className="text-[10px] text-lime-400 font-bold block">
              {preferredZone?.freqPct}% of total shot attempts
            </span>
          </div>
        </div>

        <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 flex items-center space-x-3">
          <div className="p-3 bg-lime-400/20 text-lime-400 rounded-xl">
            <Target className="w-6 h-6" />
          </div>
          <div>
            <span className="text-[10px] text-indigo-300/70 uppercase font-black tracking-wider block">
              Overall Field Goal Efficiency
            </span>
            <span className="text-sm font-black italic text-lime-400 uppercase">
              {totalMade} / {totalFg} FG ({Math.round((totalMade / totalFg) * 100)}%)
            </span>
            <span className="text-[10px] text-indigo-300/60 font-semibold block">
              3PT: {threeMade}/{threeAtt} ({threeAtt > 0 ? Math.round((threeMade/threeAtt)*100) : 0}%)
            </span>
          </div>
        </div>

        <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 flex items-center space-x-3">
          <div className="p-3 bg-sky-400/20 text-sky-400 rounded-xl">
            <Zap className="w-6 h-6" />
          </div>
          <div>
            <span className="text-[10px] text-indigo-300/70 uppercase font-black tracking-wider block">
              Hovered / Selected Zone
            </span>
            <span className="text-sm font-black italic text-sky-300 uppercase truncate block">
              {hoveredZone?.name || selectedZone?.name || 'Hover over court zones'}
            </span>
            <span className="text-[10px] text-indigo-200/80 font-bold block">
              {hoveredZone ? `${hoveredZone.fgPct}% FG (${hoveredZone.makes}/${hoveredZone.attempts})` : 'Click zone for breakdown'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Heatmap Visualization Layout: Court SVG + Breakdown Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Court Canvas Area (7 cols) */}
        <div className="lg:col-span-7 bg-indigo-950/90 p-4 rounded-3xl border border-white/10 shadow-2xl relative">
          <div className="flex items-center justify-between mb-3 px-2">
            <span className="text-xs font-black italic uppercase text-lime-400 flex items-center space-x-1">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Interactive Half-Court Vector Map</span>
            </span>

            {/* Legend Indicators */}
            <div className="flex items-center space-x-3 text-[10px] font-bold">
              {viewMode === 'efficiency' && (
                <>
                  <span className="flex items-center"><span className="w-2.5 h-2.5 rounded bg-lime-400 mr-1" /> &gt;55%</span>
                  <span className="flex items-center"><span className="w-2.5 h-2.5 rounded bg-sky-400 mr-1" /> 45-54%</span>
                  <span className="flex items-center"><span className="w-2.5 h-2.5 rounded bg-amber-400 mr-1" /> 38-44%</span>
                  <span className="flex items-center"><span className="w-2.5 h-2.5 rounded bg-rose-500 mr-1" /> &lt;38%</span>
                </>
              )}
              {viewMode === 'density' && (
                <>
                  <span className="flex items-center"><span className="w-2.5 h-2.5 rounded bg-rose-500 mr-1" /> High Vol</span>
                  <span className="flex items-center"><span className="w-2.5 h-2.5 rounded bg-orange-500 mr-1" /> Med Vol</span>
                  <span className="flex items-center"><span className="w-2.5 h-2.5 rounded bg-sky-400 mr-1" /> Low Vol</span>
                </>
              )}
            </div>
          </div>

          <div ref={containerRef} className="w-full h-[380px] sm:h-[420px] flex items-center justify-center">
            <svg ref={svgRef} className="w-full h-full" />
          </div>

          <p className="text-[10px] text-center text-indigo-300/60 italic font-semibold mt-2">
            💡 Move your cursor over court zones to view specific field goal percentages & shot volume breakdowns
          </p>
        </div>

        {/* Court Zone Detailed Breakdown List (5 cols) */}
        <div className="lg:col-span-5 space-y-3">
          <h3 className="text-xs font-black italic uppercase text-indigo-200 tracking-wider flex items-center space-x-1.5">
            <BarChart2 className="w-4 h-4 text-lime-400" />
            <span>Court Zone Performance Matrix</span>
          </h3>

          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {zones.map((zone) => {
              const isHovered = hoveredZone?.id === zone.id;
              const isSelected = selectedZone?.id === zone.id;

              const ratingBadge = {
                hot: { bg: 'bg-lime-400 text-black', label: '🔥 Hot Zone' },
                warm: { bg: 'bg-sky-400 text-black', label: '⚡ Solid' },
                cool: { bg: 'bg-amber-400 text-black', label: '📊 Moderate' },
                cold: { bg: 'bg-rose-500 text-white', label: '❄️ Cold Zone' },
              }[zone.rating];

              return (
                <div
                  key={zone.id}
                  onMouseEnter={() => setHoveredZone(zone)}
                  onMouseLeave={() => setHoveredZone(null)}
                  onClick={() => setSelectedZone(zone)}
                  className={`p-3 rounded-2xl border transition cursor-pointer ${
                    isHovered || isSelected
                      ? 'bg-indigo-900 border-lime-400 shadow-lg scale-[1.01]'
                      : 'bg-indigo-950/80 border-white/5 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-extrabold text-xs text-white uppercase">
                      {zone.name}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${ratingBadge.bg}`}>
                      {ratingBadge.label}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center text-[11px] font-bold py-1 bg-indigo-950/60 rounded-xl mb-2">
                    <div>
                      <span className="text-[9px] text-indigo-300/60 block uppercase font-black">FG %</span>
                      <span className="text-lime-400 font-black italic">{zone.fgPct}%</span>
                    </div>
                    <div>
                      <span className="text-[9px] text-indigo-300/60 block uppercase font-black">Makes/Att</span>
                      <span className="text-white font-black italic">{zone.makes}/{zone.attempts}</span>
                    </div>
                    <div>
                      <span className="text-[9px] text-indigo-300/60 block uppercase font-black">Offense Share</span>
                      <span className="text-sky-300 font-black italic">{zone.freqPct}%</span>
                    </div>
                  </div>

                  {/* Frequency Share Bar */}
                  <div className="w-full bg-indigo-950 h-1.5 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-lime-400 to-sky-400 rounded-full"
                      style={{ width: `${Math.min(100, zone.freqPct * 3)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
    </ProRestrictedWrapper>
  );
};
